import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, ShieldCheck } from 'lucide-react';
import type { IssuedTokens, SessionUser } from '@his/shared';
import { post, session } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { FormScreen, FormSection } from '@/components/ui/form-screen';
import { Dialog } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Alert, Button, Field, Input, PasswordInput } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

type Translate = (key: string) => string;

function passwordRules(t: Translate, value: string) {
  return [
    { label: t('security.rule_length'), ok: value.length >= 10 },
    { label: t('security.rule_upper'), ok: /[A-Z]/.test(value) },
    { label: t('security.rule_lower'), ok: /[a-z]/.test(value) },
    { label: t('security.rule_digit'), ok: /\d/.test(value) },
    { label: t('security.rule_symbol'), ok: /[^A-Za-z0-9]/.test(value) },
  ];
}

export function SecurityPage() {
  const { user, loading, signOut, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [dialog, setDialog] = useState<'enrol' | 're-enrol' | 'off' | null>(null);

  useEffect(() => {
    if (!loading && !user) navigate('/login', { replace: true });
    if (user?.isPlatformUser) navigate('/owner', { replace: true });
  }, [loading, user, navigate]);

  if (!user || user.isPlatformUser) return null;
  const readOnly = Boolean(user.impersonation) || user.readOnlyReason === 'cloud_mirror';

  return (
    <FormScreen title={t('security.title')} description={t('security.description')} icon={ShieldCheck} backTo="/dashboard">
      {readOnly && <Alert>{t('security.password_hint')}</Alert>}
      <PasswordSection t={t} disabled={readOnly} onChanged={async () => {
        await signOut();
        navigate('/login', { replace: true });
      }} />
      <FormSection title={t('security.two_factor_title')} icon={ShieldCheck} columns={1}>
        <Switch
          checked={user.has2fa}
          disabled={readOnly || (user.has2fa && user.requires2fa)}
          onCheckedChange={(next) => setDialog(next ? 'enrol' : 'off')}
          label={user.has2fa ? t('security.two_factor_on') : t('security.two_factor_off')}
          description={user.has2fa ? t('security.two_factor_on_hint') : t('security.two_factor_off_hint')}
        />
        {user.has2fa && user.requires2fa && <p className="text-xs text-muted-foreground">{t('security.two_factor_mandated')}</p>}
        {user.has2fa && (
          <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
            <Button type="button" variant="secondary" disabled={readOnly} onClick={() => setDialog('re-enrol')}>
              {t('security.change_phone')}
            </Button>
            <span className="text-xs text-muted-foreground">{t('security.change_phone_hint')}</span>
          </div>
        )}
      </FormSection>
      {(dialog === 'enrol' || dialog === 're-enrol') && (
        <EnrolDialog t={t} user={user} reEnrol={dialog === 're-enrol'} onClose={() => setDialog(null)} onDone={refreshUser} />
      )}
      {dialog === 'off' && <TurnOffDialog t={t} onClose={() => setDialog(null)} onDone={refreshUser} />}
    </FormScreen>
  );
}

function PasswordSection({ t, disabled, onChanged }: { t: Translate; disabled: boolean; onChanged: () => Promise<void> }) {
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rules = passwordRules(t, newPassword);
  const mismatch = confirm.length > 0 && newPassword !== confirm;
  const ready = !disabled && currentPassword.length >= 8 && rules.every((rule) => rule.ok) && newPassword === confirm;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await post('/auth/change-password', { currentPassword, newPassword, confirmPassword: confirm });
      toast.success(t('security.password_changed'), t('security.sign_in_again'));
      await onChanged();
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormSection title={t('security.password_title')} description={t('security.password_hint')} icon={KeyRound}>
      <form onSubmit={submit} className="contents">
        <Field label={t('password.current')} required className="sm:col-span-2">
          <PasswordInput autoComplete="current-password" value={currentPassword} disabled={disabled} onChange={(e) => setCurrentPassword(e.target.value)} />
        </Field>
        <Field label={t('password.new')} required>
          <PasswordInput autoComplete="new-password" value={newPassword} disabled={disabled} onChange={(e) => setNewPassword(e.target.value)} />
        </Field>
        <Field label={t('password.confirm')} required error={mismatch ? t('security.passwords_differ') : undefined}>
          <PasswordInput autoComplete="new-password" value={confirm} disabled={disabled} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <ul className="space-y-1 text-xs sm:col-span-2" aria-live="polite">
          {rules.map((rule) => (
            <li key={rule.label} className={rule.ok ? 'flex items-center gap-1.5 text-success' : 'flex items-center gap-1.5 text-muted-foreground'}>
              <span aria-hidden className="inline-block w-4 text-center font-semibold">{rule.ok ? '✓' : '○'}</span>
              {rule.label}
            </li>
          ))}
        </ul>
        {error && <Alert variant="destructive" className="sm:col-span-2">{error}</Alert>}
        <div className="sm:col-span-2">
          <Button type="submit" loading={busy} disabled={!ready}>
            {t('password.save')}
          </Button>
        </div>
      </form>
    </FormSection>
  );
}

function CodeInput({ id, value, onChange, autoFocus }: { id: string; value: string; onChange: (value: string) => void; autoFocus?: boolean }) {
  return (
    <Input
      id={id}
      dir="ltr"
      inputMode="numeric"
      maxLength={6}
      className="tracking-[0.4em]"
      value={value}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
    />
  );
}

function EnrolDialog({
  t,
  user,
  reEnrol,
  onClose,
  onDone,
}: {
  t: Translate;
  user: SessionUser;
  reEnrol: boolean;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const toast = useToast();
  const [currentCode, setCurrentCode] = useState('');
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (reEnrol) return;
    let cancelled = false;
    post<{ secret: string; otpauthUrl: string }>('/auth/2fa/setup', {})
      .then((result) => {
        if (!cancelled) setSetup(result);
      })
      .catch((caught) => {
        if (!cancelled) setError(describeError(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [reEnrol]);

  const begin = async () => {
    setBusy(true);
    setError(null);
    try {
      setSetup(await post<{ secret: string; otpauthUrl: string }>('/auth/2fa/setup', { currentTotp: currentCode }));
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const enrolled = await post<{ tokens: IssuedTokens; user: SessionUser } | null>('/auth/2fa/enable', { totp: code });
      if (enrolled?.tokens) session.setTokens(enrolled.tokens);
      await onDone();
      toast.success(t('security.enrolled'), t('security.keep_authenticator'));
      onClose();
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  };

  const showingSecret = setup !== null;
  return (
    <Dialog
      open
      onClose={onClose}
      icon={ShieldCheck}
      title={t('security.enrol_title')}
      description={showingSecret ? t('security.enrol_body') : reEnrol ? t('security.re_enrol_hint') : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('security.cancel')}
          </Button>
          {showingSecret ? (
            <Button onClick={confirm} loading={busy} disabled={code.length !== 6}>
              {t('security.confirm')}
            </Button>
          ) : (
            <Button onClick={begin} loading={busy} disabled={currentCode.length !== 6}>
              {t('security.re_enrol')}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {!showingSecret && reEnrol && (
          <Field label={t('security.current_code')} htmlFor="current-totp" required>
            <CodeInput id="current-totp" value={currentCode} onChange={setCurrentCode} autoFocus />
          </Field>
        )}
        {showingSecret && (
          <>
            <div className="rounded-md border bg-muted/40 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('security.secret_key')}</p>
              <p dir="ltr" className="mt-1 select-all break-all font-mono text-sm">{setup.secret}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                {t('security.account')}: {user.email ?? user.displayName}
              </p>
              <a className="mt-2 inline-block text-xs text-primary underline-offset-4 hover:underline" href={setup.otpauthUrl}>
                {t('security.open_in_app')}
              </a>
            </div>
            <Field label={t('security.code')} htmlFor="totp" required>
              <CodeInput id="totp" value={code} onChange={setCode} autoFocus />
            </Field>
          </>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function TurnOffDialog({ t, onClose, onDone }: { t: Translate; onClose: () => void; onDone: () => Promise<void> }) {
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await post('/auth/2fa/disable', { password, totp: code });
      await onDone();
      toast.success(t('security.turned_off'));
      onClose();
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      icon={ShieldCheck}
      title={t('security.turn_off_title')}
      description={t('security.turn_off_body')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('security.cancel')}
          </Button>
          <Button variant="destructive" onClick={submit} loading={busy} disabled={password.length < 8 || code.length !== 6}>
            {t('security.turn_off')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('signin.password')} htmlFor="off-password" required>
          <PasswordInput id="off-password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
        </Field>
        <Field label={t('security.current_code')} htmlFor="off-totp" required>
          <CodeInput id="off-totp" value={code} onChange={setCode} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
